import isMobile from '$/utils/isMobile';
import React from 'react';
import { WebinarDimensions } from './context';
import ResizeObserver from 'resize-observer-polyfill';
import { logger } from '$/utils/logger';

export const MaxScaledRoomWidth = isMobile ? 450 : 700;
export const MaxScaledRoomHeight = 575;

export type ScaledRoomSizeInfo = {
  ref: (node: HTMLDivElement) => void;
  height: number;
  width: number;
};

export type ScaleRoomDimensions = {
  bodySize: ScaledRoomSizeInfo;
  footerSize: ScaledRoomSizeInfo;
  scale: number;
  overlayScale: number;
  isMeasured: boolean;
};

export const waitForImagesInDivLoaded = (node: HTMLDivElement, onLoaded: () => void): void => {
  const images = node.querySelectorAll('img');
  if (images.length === 0) {
    onLoaded();
  } else {
    // wait for the image loads
    let loaded = 0;
    images.forEach((img) => {
      img.onload = (): void => {
        loaded += 1;
        if (loaded === images.length) {
          onLoaded();
        }
      };
    });
  }
};

export function useScaledRoomScale(
  fullScreen: boolean | undefined,
  dimensions: WebinarDimensions
): ScaleRoomDimensions {
  const isUnMounted = React.useRef(false);
  const [bodyDimensions, setBodyDimensions] = React.useState({
    width: MaxScaledRoomWidth,
    height: MaxScaledRoomHeight,
    measured: false,
  });
  const [footerDimensions, setFooterDimensions] = React.useState({
    width: MaxScaledRoomWidth,
    height: 0,
    measured: false,
  });

  const bodyObserver = React.useRef(
    new ResizeObserver((entries) => {
      if (isUnMounted.current) return;
      setBodyDimensions({
        width: entries[0].contentRect.width,
        height: entries[0].contentRect.height,
        measured: true,
      });
    })
  );

  const refRoomBody = React.useCallback<(node: HTMLDivElement) => void>((node) => {
    if (!node) return;
    const observer = bodyObserver.current;

    waitForImagesInDivLoaded(node, () => {
      observer.observe(node);
      setBodyDimensions({ width: node.clientWidth, height: node.clientHeight, measured: true });
    });

    return (): void => {
      observer.unobserve(node);
    };
  }, []);

  const footerObserver = React.useRef(
    new ResizeObserver((entries) => {
      if (isUnMounted.current) return;

      setFooterDimensions({
        width: entries[0].target.clientWidth,
        height: entries[0].target.clientHeight,
        measured: true,
      });
    })
  );

  const refRoomFooter = React.useCallback<(node: HTMLDivElement) => void>((node) => {
    if (!node) return;
    const observer = footerObserver.current;
    waitForImagesInDivLoaded(node, () => {
      setFooterDimensions({ width: node.clientWidth, height: node.clientHeight, measured: true });
      observer.observe(node);
    });

    return (): void => {
      observer.unobserve(node);
    };
  }, []);

  const [scaleDimensions, setScaleDimensions] = React.useState<ScaleRoomDimensions>({
    scale: isMobile ? 0.9 : 1,
    overlayScale: isMobile ? 0.7 : 1,
    bodySize: { ref: refRoomBody, ...bodyDimensions },
    footerSize: { ref: refRoomFooter, ...footerDimensions },
    isMeasured: false,
  });

  React.useEffect(() => {
    return (): void => {
      isUnMounted.current = true;
    };
  }, []);

  React.useEffect(() => {
    const container = {
      width: fullScreen ? dimensions.webinarArea.width : dimensions.videoArea.width,
      height: fullScreen ? dimensions.webinarArea.height : dimensions.videoArea.height,
    };

    const width = Math.max(bodyDimensions.width, footerDimensions.width);
    const wScale = container.width / width;
    const height = bodyDimensions.height + footerDimensions.height;
    const hScale = container.height / (height + 1);
    const overlayScale = Math.min(
      1,
      Math.min(
        container.height / (MaxScaledRoomWidth * (3 / 4)),
        container.width / MaxScaledRoomWidth
      )
    );

    if (isUnMounted.current) return;

    const scale = Math.min(Math.min(Math.min(wScale, hScale)), 1);

    logger.debug('ANIM', 'useScaledRoomScale', {
      scale,
      hScale,
      wScale,
      overlayScale,
    });

    setScaleDimensions({
      bodySize: {
        ref: refRoomBody,
        height: Math.round(bodyDimensions.height),
        width: Math.round(bodyDimensions.width),
      },
      footerSize: {
        ref: refRoomFooter,
        height: Math.round(footerDimensions.height),
        width: Math.round(footerDimensions.width),
      },
      scale,
      overlayScale,
      isMeasured: bodyDimensions.measured,
    });
  }, [bodyDimensions, dimensions, footerDimensions, fullScreen, refRoomBody, refRoomFooter]);

  return scaleDimensions;
}

export function useVideoStageAspectScale(dimensions: WebinarDimensions): number {
  const [aspectScale, setAspectScale] = React.useState(1);

  React.useEffect(() => {
    const base =
      dimensions.actionsPane.location === 'bottom'
        ? { w: 500, h: 400 }
        : isMobile
          ? { w: 600, h: 300 }
          : { w: 700, h: 600 };

    const width = dimensions.videoStage.width / dimensions.videoStage.scale;
    const height = dimensions.videoStage.height / dimensions.videoStage.scale;

    const wScale = width / base.w;
    const hScale = height / base.h;

    // DD: Scale for VideoStage has to be a percentage of the player.scale as player.scale grows the bigger
    // the differene between the scaled player and the window size
    const scale = Math.min(Math.min(wScale, hScale), 1.25);

    let finalScale = dimensions.videoStage.scale * scale;
    if (isMobile) {
      finalScale = Math.max(0.5, scale);
    }

    setAspectScale(finalScale);
  }, [
    dimensions.videoArea.width,
    dimensions.videoArea.height,
    dimensions.videoStage.width,
    dimensions.videoStage.height,
    dimensions.video.aspectRatio,
    dimensions.videoStage.scale,
    dimensions.actionsPane.location,
  ]);

  return aspectScale;
}
